import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BikeTypeSearch } from '../../../models/BikeType';
import { Option } from '../../../shared/data-filters/data-filter-select/data-filter-select.component';
import { ValidationUtils } from '../../../shared/validation.utils';
import { TablesService } from '../shared/tables.service';

@Component({
    selector: 'app-tables-update',
    templateUrl: './tables-update.component.html',
    styleUrls: ['./tables-update.component.scss'],
    standalone: false
})
export class TablesUpdateComponent implements OnInit {

  public tableCode: string;
  public id: string;
  public tableForm: FormGroup;
  public errorArray = [];
  public productTypeOptions: Option[] = [];

  constructor(private _translate: TranslateService, private _route: ActivatedRoute, private _tablesService: TablesService,
    public _snackBar: MatSnackBar, private _formBuilder: FormBuilder, private _router: Router) {

      this._route.params.subscribe(params => {
        this.tableCode = params['tableCode'];
        this.id = params['recordId'];
      });

    }

  public ngOnInit(): void {

      switch (this.tableCode) {
        case 'bikeType':
          this._setFormBikeType();
          break;
        case 'bikeBrand':
          this._setFormBikeBrand();
          break;
        case 'bikeModel':
          this._setFormBikeModel();
          break;
        case 'bikeSize':
          this._setFormBikeSize();
          break;
        case 'tyreSize':
          this._setFormTyreSize();
          break;
        case 'bikeColor':
          this._setFormBikeColor();
          break;
      }
  }


  public onSubmitHandler() {
    this.tableForm.markAllAsTouched();
    if (this.tableForm.valid) {
      switch (this.tableCode) {
        case 'bikeType':
          this._updateBikeType();
        break;
        case 'bikeBrand':
          this._updateBikeBrand();
        break;
        case 'bikeModel':
          this._updateBikeModel();
        break;
        case 'bikeSize':
          this._updateBikeSize();
        break;
        case 'tyreSize':
          this._updateTyreSize();
        break;
        case 'bikeColor':
          this._updateBikeColor();
        break;
      }
    } else {
      this._manageErrors();
    }

  }

  private _setFormBikeType() {
    this.tableForm = this._formBuilder.group({
      bikeType_id: [{ value: '', disabled: true }, <any>Validators.required],
      bikeType_name_en: ['', <any>Validators.required],
      bikeType_name_it: ['', <any>Validators.required],
      bikeType_name_de: ['', <any>Validators.required],
      bikeType_bikeCost: ['', [Validators.required, Validators.min(0), Validators.pattern('^(0|[1-9][0-9]*)$')]],
      bikeType_productType: [1, [Validators.required, Validators.min(1), Validators.max(2), Validators.pattern('^(0|[1-9][0-9]*)$')]],
      // bikeType_bikeAvailabilityPeriod: [null, [Validators.required, Validators.min(0), Validators.pattern('^(0|[1-9][0-9]*)$')]],
    });

    this.productTypeOptions.push({ value: 1, label: 'bike' }, { value: 2, label: 'equipment' });

    this._tablesService.getBikeTypeById(this.id).subscribe(result => {
      this.tableForm.controls['bikeType_id'].setValue(result.id);
      this.tableForm.controls['bikeType_name_en'].setValue(result.name_en);
      this.tableForm.controls['bikeType_name_it'].setValue(result.name_it);
      this.tableForm.controls['bikeType_name_de'].setValue(result.name_de);
      this.tableForm.controls['bikeType_bikeCost'].setValue(result.bikeCost);
      this.tableForm.controls['bikeType_productType'].setValue(result.productType);
      // this.tableForm.controls['bikeType_bikeAvailabilityPeriod'].setValue(result.bikeAvailabilityPeriod);
    });
  }

  private _setFormBikeBrand() {
    this.tableForm = this._formBuilder.group({
      bikeBrand_id: [{ value: '', disabled: true }, <any>Validators.required],
      bikeBrand_name: ['', <any>Validators.required]
    });

    this._tablesService.getBikeBrandById(this.id).subscribe(result => {
      this.tableForm.controls['bikeBrand_id'].setValue(result.id);
      this.tableForm.controls['bikeBrand_name'].setValue(result.name);
    });
  }

  private _setFormBikeModel() {
    this.tableForm = this._formBuilder.group({
      bikeModel_id: [{ value: '', disabled: true }, <any>Validators.required],
      bikeModel_name: ['', <any>Validators.required]
    });

    this._tablesService.getBikeModelById(this.id).subscribe(result => {
      this.tableForm.controls['bikeModel_id'].setValue(result.id);
      this.tableForm.controls['bikeModel_name'].setValue(result.name);
    });
  }

  private _setFormBikeSize() {
    this.tableForm = this._formBuilder.group({
      bikeSize_id: [{ value: '', disabled: true }, <any>Validators.required],
      bikeSize_size: ['', <any>Validators.required]
    });

    this._tablesService.getBikeSizeById(this.id).subscribe(result => {
      this.tableForm.controls['bikeSize_id'].setValue(result.id);
      this.tableForm.controls['bikeSize_size'].setValue(result.size);
    });
  }

  private _setFormTyreSize() {
    this.tableForm = this._formBuilder.group({
      tyreSize_id: [{ value: '', disabled: true }, <any>Validators.required],
      tyreSize_size: ['', <any>Validators.required]
    });

    this._tablesService.getTyreSizeById(this.id).subscribe(result => {
      this.tableForm.controls['tyreSize_id'].setValue(result.id);
      this.tableForm.controls['tyreSize_size'].setValue(result.size);
    });
  }

  private _setFormBikeColor() {
    this.tableForm = this._formBuilder.group({
      bikeColor_id: [{ value: '', disabled: true }, <any>Validators.required],
      bikeColor_name: ['', <any>Validators.required]
    });

    this._tablesService.getBikeColorById(this.id).subscribe(result => {
      this.tableForm.controls['bikeColor_id'].setValue(result.id);
      this.tableForm.controls['bikeColor_name'].setValue(result.name);
    });
  }

  private _updateBikeType() {
    const bikeTypeObject: BikeTypeSearch = new BikeTypeSearch();
    bikeTypeObject.id = this.tableForm.controls['bikeType_id'].value;
    bikeTypeObject.name_en = this.tableForm.controls['bikeType_name_en'].value;
    bikeTypeObject.name_it = this.tableForm.controls['bikeType_name_it'].value;
    bikeTypeObject.name_de = this.tableForm.controls['bikeType_name_de'].value;
    bikeTypeObject.bikeCost = this.tableForm.controls['bikeType_bikeCost'].value;
    bikeTypeObject.productType = this.tableForm.controls['bikeType_productType'].value;
    bikeTypeObject.bikeAvailabilityPeriod = 3;

    console.log('bikeTypeObject:', bikeTypeObject);

    this._tablesService.updateBikeType(bikeTypeObject).subscribe({
        next: (result) => {
          this._manageAfterUpdate(result);
        },
        error: (error) => {
          this._manageApiError(error);
        }
    });
  }

  private _updateBikeBrand() {
    const bikeBrandObject: any = {};
    bikeBrandObject.type = 'brand'; // fixed
    bikeBrandObject.id = this.tableForm.controls['bikeBrand_id'].value;
    bikeBrandObject.name = this.tableForm.controls['bikeBrand_name'].value;
    bikeBrandObject.size = null;
    console.log('bikeBrandObject:', bikeBrandObject);

    this._tablesService.updateBikeProperty(bikeBrandObject).subscribe({
        next: (result) => {
          this._manageAfterUpdate(result);
        },
        error: (error) => {
          this._manageApiError(error);
        }
    });
  }

  private _updateBikeModel() {
    const bikeModelObject: any = {};
    bikeModelObject.type = 'model'; // fixed
    bikeModelObject.id = this.tableForm.controls['bikeModel_id'].value;
    bikeModelObject.name = this.tableForm.controls['bikeModel_name'].value;
    bikeModelObject.size = null;
    console.log('bikeModelObject:', bikeModelObject);

    this._tablesService.updateBikeProperty(bikeModelObject).subscribe({
        next: (result) => {
          this._manageAfterUpdate(result);
        },
        error: (error) => {
          this._manageApiError(error);
        }
    });
  }

  private _updateBikeSize() {
    const bikeSizeObject: any = {};
    bikeSizeObject.type = 'size'; // fixed
    bikeSizeObject.id = this.tableForm.controls['bikeSize_id'].value;
    bikeSizeObject.name = null;
    bikeSizeObject.size = this.tableForm.controls['bikeSize_size'].value;
    console.log('bikeSizeObject:', bikeSizeObject);

    this._tablesService.updateBikeProperty(bikeSizeObject).subscribe({
        next: (result) => {
          this._manageAfterUpdate(result);
        },
        error: (error) => {
          this._manageApiError(error);
        }
    });
  }

  private _updateTyreSize() {
    const tyreSizeObject: any = {};
    tyreSizeObject.type = 'tyreSize'; // fixed
    tyreSizeObject.id = this.tableForm.controls['tyreSize_id'].value;
    tyreSizeObject.name = null;
    tyreSizeObject.size = this.tableForm.controls['tyreSize_size'].value;
    console.log('tyreSizeObject:', tyreSizeObject);

    this._tablesService.updateBikeProperty(tyreSizeObject).subscribe({
        next: (result) => {
          this._manageAfterUpdate(result);
        },
        error: (error) => {
          this._manageApiError(error);
        }
    });
  }

  private _updateBikeColor() {
    const bikeColorObject: any = {};
    bikeColorObject.type = 'color'; // fixed
    bikeColorObject.id = this.tableForm.controls['bikeColor_id'].value;
    bikeColorObject.name = this.tableForm.controls['bikeColor_name'].value;
    bikeColorObject.size = null;
    console.log('bikeColorObject:', bikeColorObject);

    this._tablesService.updateBikeProperty(bikeColorObject).subscribe({
        next: (result) => {
          this._manageAfterUpdate(result);
        },
        error: (error) => {
          this._manageApiError(error);
        }
    });
  }

  private _manageAfterUpdate(result: any) {
    this.tableForm.clearValidators();
    this.errorArray = [];
    this._snackBar.open(this._translate.instant('tableData.form.messageUpdateCompleted'), '', {
      duration: 2000,
    }).afterDismissed().subscribe(() => {
      this._router.navigate(['/table-data-management/list/', this.tableCode]);
    });
  }

  private _manageApiError(error: any) {
    this.tableForm.clearValidators();
    this.errorArray = [];
    this._snackBar.open(this._translate.instant('Internal server error!/n' + error.error), '', {
      duration: 3500,
    });
  }

  private _manageErrors() {
    this.errorArray = ValidationUtils.getFormValidationErrors(this.tableForm, this.tableForm.controls);
    this._snackBar.open(this._translate.instant('tableData.form.messageUpdateError'), '', {
      duration: 3500,
    }).afterDismissed().subscribe(() => {
      this.tableForm.clearValidators();
      this.errorArray = [];
    });
  }

}
